import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { EducationLevelRadioGroup } from './EducationLevelRadioGroup';
import { TeacherSearchSelect, TeacherInfo } from './TeacherSearchSelect';
import { useClassTemplateMutations } from '@/hooks/useClassTemplateMutations';
import { ClassTemplate } from '@/hooks/useClassTemplates';
import { useSubjects } from '@/hooks/useSubjects';
import {
  WEEKDAYS,
  COURSE_TYPES,
  CLASS_TYPES,
  CLASS_LANGUAGES,
  LOCATIONS,
} from '@/lib/education-levels';

interface FormValues {
  teacher_code: string;
  weekday: number;
  start_time: string;
  end_time: string;
  course_type: 'REGULAR' | 'INTER';
  class_type: 'PREMIER' | 'PRIVATE';
  education_level: string;
  language: 'THAI' | 'ENGLISH';
  subject: string;
  location: string;
  room_name: string;
  is_special_class: boolean;
  fixed_cost: number | null;
  note: string;
  student_codes: string[];
}

interface ClassTemplateFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template?: ClassTemplate | null;
  defaultWeekday?: number;
}

export function ClassTemplateFormDialog({
  open,
  onOpenChange,
  template,
  defaultWeekday = 1,
}: ClassTemplateFormDialogProps) {
  const { createTemplate, updateTemplate } = useClassTemplateMutations();
  const { data: subjects, isLoading: subjectsLoading } = useSubjects();
  const isEditing = !!template;

  const [selectedTeacher, setSelectedTeacher] = useState<TeacherInfo | null>(null);

  const form = useForm<FormValues>({
    defaultValues: {
      teacher_code: '',
      weekday: defaultWeekday,
      start_time: '09:00',
      end_time: '11:00',
      course_type: 'REGULAR',
      class_type: 'PREMIER',
      education_level: 'ม.1',
      language: 'THAI',
      subject: '',
      location: '',
      room_name: '',
      is_special_class: false,
      fixed_cost: null,
      note: '',
      student_codes: [],
    },
  });

  useEffect(() => {
    if (template) {
      form.reset({
        teacher_code: template.teacher_code,
        weekday: template.weekday,
        start_time: template.start_time,
        end_time: template.end_time,
        course_type: template.course_type as 'REGULAR' | 'INTER',
        class_type: template.class_type as 'PREMIER' | 'PRIVATE',
        education_level: template.education_level,
        language: template.language,
        subject: template.subject,
        location: template.location,
        room_name: template.room_name || '',
        is_special_class: template.is_special_class,
        fixed_cost: template.fixed_cost,
        note: template.note || '',
        student_codes: template.students?.map((s) => s.code) || [],
      });
    } else {
      form.reset({
        teacher_code: '',
        weekday: defaultWeekday,
        start_time: '09:00',
        end_time: '11:00',
        course_type: 'REGULAR',
        class_type: 'PREMIER',
        education_level: 'ม.1',
        language: 'THAI',
        subject: '',
        location: '',
        room_name: '',
        is_special_class: false,
        fixed_cost: null,
        note: '',
        student_codes: [],
      });
      setSelectedTeacher(null);
    }
  }, [template, defaultWeekday, form]);

  const isSpecialClass = form.watch('is_special_class');

  const onSubmit = async (values: FormValues) => {
    const data = {
      ...values,
      fixed_cost: values.is_special_class ? values.fixed_cost : null,
      room_name: values.room_name || undefined,
      note: values.note || undefined,
    };

    if (isEditing && template) {
      await updateTemplate.mutateAsync({ id: template.id, ...data });
    } else {
      await createTemplate.mutateAsync(data);
    }

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'แก้ไขตารางเรียน' : 'เพิ่มตารางเรียนใหม่'}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="teacher_code"
                rules={{ required: 'กรุณาเลือกครู' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>รหัสครู</FormLabel>
                    <FormControl>
                      <TeacherSearchSelect
                        value={field.value}
                        onChange={(code, teacher) => {
                          field.onChange(code);
                          setSelectedTeacher(teacher);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="weekday"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>วัน</FormLabel>
                    <Select
                      value={String(field.value)}
                      onValueChange={(v) => field.onChange(Number(v))}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {WEEKDAYS.map((day) => (
                          <SelectItem key={day.key} value={day.key}>
                            {day.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="start_time"
                rules={{ required: 'กรุณาระบุเวลาเริ่ม' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>เวลาเริ่ม</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_time"
                rules={{ required: 'กรุณาระบุเวลาจบ' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>เวลาจบ</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="course_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ประเภทคอร์ส</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      className="flex gap-4"
                    >
                      {COURSE_TYPES.map((type) => (
                        <div key={type.value} className="flex items-center space-x-2">
                          <RadioGroupItem value={type.value} id={`course-${type.value}`} />
                          <Label htmlFor={`course-${type.value}`}>{type.label}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="class_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ประเภทคลาส</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      className="flex gap-4"
                    >
                      {CLASS_TYPES.map((type) => (
                        <div key={type.value} className="flex items-center space-x-2">
                          <RadioGroupItem value={type.value} id={`class-${type.value}`} />
                          <Label htmlFor={`class-${type.value}`}>{type.label}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="education_level"
              rules={{ required: 'กรุณาเลือกระดับชั้น' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ระดับชั้น</FormLabel>
                  <FormControl>
                    <EducationLevelRadioGroup value={field.value} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="language"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ภาษา</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      className="flex gap-4"
                    >
                      {CLASS_LANGUAGES.map((lang) => (
                        <div key={lang.value} className="flex items-center space-x-2">
                          <RadioGroupItem value={lang.value} id={`lang-${lang.value}`} />
                          <Label htmlFor={`lang-${lang.value}`}>{lang.label}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="subject"
              rules={{ required: 'กรุณาเลือกวิชา' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>วิชา</FormLabel>
                  <FormControl>
                    {subjectsLoading ? (
                      <p className="text-sm text-muted-foreground">กำลังโหลด...</p>
                    ) : (
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        className="flex flex-wrap gap-3"
                      >
                        {subjects?.map((subj) => (
                          <div key={subj.name} className="flex items-center space-x-2">
                            <RadioGroupItem value={subj.name} id={`subject-${subj.name}`} />
                            <Label htmlFor={`subject-${subj.name}`}>{subj.name}</Label>
                          </div>
                        ))}
                      </RadioGroup>
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="location"
              rules={{ required: 'กรุณาเลือกสาขา' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>สาขา</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      className="flex gap-4"
                    >
                      {LOCATIONS.map((loc) => (
                        <div key={loc.value} className="flex items-center space-x-2">
                          <RadioGroupItem value={loc.value} id={`location-${loc.value}`} />
                          <Label htmlFor={`location-${loc.value}`}>{loc.label}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="room_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ห้องเรียน (ไม่บังคับ)</FormLabel>
                  <FormControl>
                    <Input placeholder="ห้อง A101" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_special_class"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel className="!mt-0">คลาสพิเศษ (กำหนดค่าสอนเอง)</FormLabel>
                </FormItem>
              )}
            />

            {isSpecialClass && (
              <FormField
                control={form.control}
                name="fixed_cost"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ค่าสอนต่อชั่วโมง (บาท)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="500"
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(e.target.value ? Number(e.target.value) : null)
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>หมายเหตุ (ไม่บังคับ)</FormLabel>
                  <FormControl>
                    <Textarea placeholder="หมายเหตุเพิ่มเติม..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                ยกเลิก
              </Button>
              <Button
                type="submit"
                disabled={createTemplate.isPending || updateTemplate.isPending}
              >
                {isEditing ? 'บันทึก' : 'เพิ่ม'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
